import { useEffect, useMemo, useState } from 'react';
import type { Candle } from './chart-data';
import { PERIOD, clamp } from './chart-data';
import type { Timeframe } from './zone-math';

type Session = { timeframe: Timeframe; candles: Candle[]; count: number };
export function useChartReplay(timeframe: Timeframe) {
  const [session, setSession] = useState<Session | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const active = session?.timeframe === timeframe ? session : null;
  useEffect(() => { setSession(null); setPlaying(false); }, [timeframe]);
  useEffect(() => {
    const pause = () => { if (document.hidden) setPlaying(false); };
    document.addEventListener('visibilitychange', pause);
    return () => document.removeEventListener('visibilitychange', pause);
  }, []);
  useEffect(() => {
    if (!playing || !active || active.count >= active.candles.length) return;
    const timer = setInterval(() => {
      if (document.hidden) return;
      setSession(current => current ? { ...current, count: Math.min(current.candles.length, current.count + 1) } : null);
    }, 1000 / speed);
    return () => clearInterval(timer);
  }, [playing, Boolean(active), speed, active?.count === active?.candles.length]);
  useEffect(() => { if (active && active.count >= active.candles.length) setPlaying(false); }, [active]);
  const rows = useMemo(() => active?.candles.slice(0, active.count) || null, [active]);
  return {
    active, rows, playing, speed, setSpeed, setPlaying,
    start(candles: Candle[]) {
      // Freeze complete, actual candles. A live tick must never rewrite a replay.
      const closed = candles.filter(candle => candle.time + PERIOD[timeframe] <= Date.now()).map(candle => ({ ...candle }));
      if (closed.length < 3) return false;
      setSession({ timeframe, candles: closed, count: clamp(Math.floor(closed.length * .7), 2, closed.length - 1) });
      setPlaying(false);
      return true;
    },
    seek(count: number) { setPlaying(false); setSession(current => current ? { ...current, count: clamp(Math.round(count), 2, current.candles.length) } : null); },
    exit() { setPlaying(false); setSession(null); },
  };
}

export function ReplayDeck({ replay, onSeek, onExit }: { replay: ReturnType<typeof useChartReplay>; onSeek: (count: number) => void; onExit: () => void }) {
  const session = replay.active;
  if (!session) return null;
  const last = session.candles[session.count - 1];
  const finished = session.count === session.candles.length;
  return <section className="zp-replay-deck" aria-label="Candle Replay">
    <div className="zp-replay-heading"><span className="zp-replay-icon" aria-hidden="true">↶</span><div><strong>Candle Replay <span>HISTORICAL</span></strong><p>Ulang kaji satu candle pada satu masa. Marking asal disimpan.</p></div><button className="zp-replay-exit" onClick={onExit} aria-label="Keluar Replay">Keluar Replay <span aria-hidden="true">×</span></button></div>
    <div className="zp-replay-controls">
      <button onClick={() => onSeek(session.count - 1)} disabled={session.count <= 2} aria-label="Replay candle sebelumnya">‹</button>
      <button className="zp-replay-play" disabled={finished} aria-label={replay.playing ? 'Pause Replay' : 'Play Replay'} onClick={() => replay.setPlaying(!replay.playing)}>{replay.playing ? 'Ⅱ Pause' : '▷ Play'}</button>
      <button onClick={() => onSeek(session.count + 1)} disabled={finished} aria-label="Replay candle seterusnya">›</button>
      <label className="zp-replay-track"><span>Kedudukan candle <b>{session.count} / {session.candles.length}</b></span><input aria-label="Kedudukan Replay" type="range" min={2} max={session.candles.length} value={session.count} onChange={e => onSeek(Number(e.target.value))} /></label>
      <label className="zp-replay-speed">Kelajuan<select aria-label="Kelajuan Replay" value={replay.speed} onChange={e => replay.setSpeed(Number(e.target.value))}>{[.5, 1, 2, 4].map(speed => <option key={speed} value={speed}>{speed}×</option>)}</select></label>
    </div>
    <div className="zp-replay-caption"><time>{new Date(last.time).toISOString().slice(0, 16).replace('T', ' ')} UTC</time><span>{finished ? 'Replay selesai · undur untuk ulang semula' : 'Candle akan datang disembunyikan'}</span></div>
  </section>;
}
